import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { X, Merge, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";

/**
 * "Merge duplicate photographers" modal.
 *
 * By default groups any pair (or more) of photographers whose
 * first_name+last_name match case-insensitively. Toggling
 * "Include near matches" widens the net to catch typos / nicknames
 * (Smyth ↔ Smith, Bill ↔ William) via a backend similarity score;
 * those groups render with a "NEAR MATCH · 87%" badge so the admin
 * can visually distinguish them from exact collisions.
 *
 * For each group the user picks which row to KEEP; every other row
 * in the group is merged into the keeper — their entries are
 * reassigned onto the kept photographer_id and the row itself is
 * deleted.
 *
 * The picker seeds the "keep" choice to whichever row has the most
 * entries (usually the original, pre-import row).
 */
export function MergeDuplicatesModal({ onClose, onMerged }) {
  const [state, setState] = useState({ loading: true, error: "", groups: [] });
  const [pickers, setPickers] = useState({}); // group-index → id-to-keep
  const [busyKey, setBusyKey] = useState(null);
  const [fuzzy, setFuzzy] = useState(false);

  const load = async (useFuzzy = fuzzy) => {
    setState({ loading: true, error: "", groups: [] });
    try {
      const { data } = await api.get("/photographers/duplicates", {
        params: useFuzzy ? { fuzzy: true, threshold: 0.85 } : {},
      });
      const initialPicks = {};
      data.groups.forEach((g, i) => { initialPicks[i] = g.photographers[0].id; });
      setPickers(initialPicks);
      setState({ loading: false, error: "", groups: data.groups });
    } catch (ex) {
      setState({
        loading: false,
        error: formatApiError(ex.response?.data?.detail) || ex.message,
        groups: [],
      });
    }
  };

  useEffect(() => { load(fuzzy); }, [fuzzy]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeGroup = async (gi) => {
    const group = state.groups[gi];
    const keepId = pickers[gi];
    const removals = group.photographers.filter((p) => p.id !== keepId);
    if (!removals.length) return;
    const label = `${group.first_name} ${group.last_name}`;
    if (!window.confirm(
      `Merge ${removals.length} duplicate row${removals.length === 1 ? "" : "s"} `
      + `into the kept photographer "${label}"? `
      + `${removals.reduce((n, r) => n + (r.entry_count || 0), 0)} entries will be reassigned. `
      + `This cannot be undone.`,
    )) return;

    setBusyKey(gi);
    try {
      for (const rem of removals) {
        await api.post("/photographers/merge", {
          keep_id: keepId,
          remove_id: rem.id,
        });
      }
      toast.success(`Merged ${label}`);
      await load(fuzzy);
      onMerged?.();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Merge failed");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={onClose}
      data-testid="merge-duplicates-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        style={{ background: "#11061d", border: "1px solid #3f3f3f" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ borderBottom: "1px solid #1f1f1f" }}
        >
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
              Cleanup
            </div>
            <div className="text-base font-semibold tracking-tight">Merge duplicate photographers</div>
          </div>
          <button
            onClick={onClose}
            className="muted-text hover:text-white p-1"
            data-testid="merge-duplicates-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          <label
            className="flex items-center gap-2 mb-4 text-xs cursor-pointer select-none"
            data-testid="merge-duplicates-fuzzy-toggle"
          >
            <input
              type="checkbox"
              checked={fuzzy}
              onChange={(e) => setFuzzy(e.target.checked)}
              className="accent-amber-400"
            />
            <span>
              Include near matches
              <span className="muted-text ml-2">
                catches typos, nicknames, initials &amp; middle names
                (S Green ↔ Stuart Green, John Doo ↔ John George Doo)
              </span>
            </span>
          </label>

          {state.loading && (
            <div className="muted-text text-sm py-10 text-center flex items-center gap-2 justify-center">
              <Loader2 size={14} className="animate-spin" /> Scanning your roster…
            </div>
          )}

          {state.error && (
            <div className="danger-text text-sm py-6" data-testid="merge-duplicates-error">
              {state.error}
            </div>
          )}

          {!state.loading && !state.error && state.groups.length === 0 && (
            <div className="py-10 text-center" data-testid="merge-duplicates-empty">
              <CheckCircle2 size={28} className="mx-auto text-emerald-400 mb-3" />
              <div className="text-sm">
                {fuzzy
                  ? "No exact or near duplicates found. Your roster is clean."
                  : "No exact duplicates found. Try turning on \u201cInclude near matches\u201d to catch typos."}
              </div>
            </div>
          )}

          {state.groups.length > 0 && (
            <>
              <div className="mb-4 flex items-start gap-2 p-3 text-xs"
                   style={{ background: "#1a1000", border: "1px solid #533b00", color: "#fbbf24" }}>
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  For each group, pick the row to <b>keep</b>. Every other row is deleted,
                  and its entries are re-assigned onto the kept photographer. This cannot be undone —
                  take a backup from Settings first if you&rsquo;re unsure.
                  <div className="mt-2 muted-text">
                    The kept row&rsquo;s <b>display name</b> is preserved exactly as shown.
                    You can still edit the kept row&rsquo;s I.D., email, phone or membership number
                    afterwards via the pencil icon.
                  </div>
                  {fuzzy && (
                    <div className="mt-2">
                      <b>Near matches are best guesses</b> — always read both rows carefully
                      before merging. Two people can genuinely have very similar names.
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {state.groups.map((g, gi) => (
                  <div key={gi}
                       className="p-4"
                       style={{ background: "#0f0f0f", border: "1px solid #2a2a2a" }}
                       data-testid={`merge-duplicates-group-${gi}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                        <span>{g.first_name} {g.last_name}</span>
                        <span className="muted-text text-xs">· {g.count} rows</span>
                        {g.exact === false && (
                          <span
                            className="mono text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5"
                            style={{ background: "#3a1f00", border: "1px solid #7a4a00", color: "#fbbf24" }}
                            data-testid={`merge-duplicates-near-badge-${gi}`}
                          >
                            near match · {Math.round((g.similarity || 0) * 100)}%
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => mergeGroup(gi)}
                        disabled={busyKey === gi}
                        className="ccs-btn-primary text-xs flex items-center gap-2 disabled:opacity-40"
                        data-testid={`merge-duplicates-merge-${gi}`}
                      >
                        {busyKey === gi ? <Loader2 size={12} className="animate-spin" /> : <Merge size={12} />}
                        Merge
                      </button>
                    </div>
                    <div className="space-y-2">
                      {g.photographers.map((p) => (
                        <label
                          key={p.id}
                          className="flex items-start gap-3 cursor-pointer p-2 hover:bg-white/5"
                          data-testid={`merge-duplicates-pick-${gi}-${p.id}`}
                        >
                          <input
                            type="radio"
                            name={`keep-${gi}`}
                            checked={pickers[gi] === p.id}
                            onChange={() => setPickers({ ...pickers, [gi]: p.id })}
                            className="mt-1 accent-amber-400"
                          />
                          <div className="flex-1 min-w-0 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="mono text-amber-400">{p.photographer_code}</span>
                              <span>{p.display_name || `${p.first_name} ${p.last_name}`}</span>
                              {pickers[gi] === p.id && (
                                <span className="mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">
                                  keep
                                </span>
                              )}
                            </div>
                            <div className="mono text-[10px] muted-text mt-0.5">
                              {p.entry_count} entr{p.entry_count === 1 ? "y" : "ies"}
                              {p.email && <> · {p.email}</>}
                              {p.tel_no && <> · {p.tel_no}</>}
                              {p.membership_number && <> · #{p.membership_number}</>}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    {/* Reassurance line: the kept row's display fields are
                        preserved verbatim, so admins can see exactly what
                        will remain on scoresheets after the merge. */}
                    {(() => {
                      const keeper = g.photographers.find((p) => p.id === pickers[gi]);
                      if (!keeper) return null;
                      return (
                        <div
                          className="mt-3 text-[11px] p-2"
                          style={{ background: "#0a1a0a", border: "1px solid #1f3a1f", color: "#9fdc9f" }}
                          data-testid={`merge-duplicates-keeper-preview-${gi}`}
                        >
                          After merge the kept photographer keeps the display name{" "}
                          <b>&ldquo;{keeper.display_name || `${keeper.first_name} ${keeper.last_name}`}&rdquo;</b>{" "}
                          (I.D. <span className="mono text-amber-400">{keeper.photographer_code}</span>)
                          — only their entry list grows. The removed row is deleted.
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
